import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { DEFAULT_MAX_LOADS_COUNT, SPLITTABLE_MAX_LOADS_COUNT } from '~constants/consts';
import {
  nameIdNonRequiredSchema,
  nameIdSchemaRequired,
  notesSchema,
  phoneSchema,
  waypointSchema,
  waypointSchemaOptionalSites,
} from '~constants/regexConst';
import { fileAttachmentSchema } from '~formsShared/components/FileAttachments';
import { Job } from '~hooks/useJob/models';
import { JobFormProps } from '~hooks/useJob/models';
import { EmptyContactItem } from '~pages/Sales/Projects/projectFormSchema';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { formQuantityFieldRepresentsEmptyQuantity } from '~utils/utilFunctions';
import { emptyStringToNull } from '~utils/yupTransforms';

import { getRateTypeLabel } from '../Settings/RatesManagement/rateUtils';

const jobFormSchema = yup.object().shape(
  {
    id: yup.string(),
    jobStartAt: yup.string().required(),
    material: yup.object().when('$isHiredTruckService', {
      is: true,
      then: () => nameIdNonRequiredSchema.notRequired(),
      otherwise: () => nameIdSchemaRequired.required(),
    }),
    equipment: yup.object().notRequired(),
    additionalEquipment: yup.object().notRequired(),
    quantity: yup.number().when('unitOfMeasure', {
      is: (uom: ItemNameAndId | null) =>
        isNil(uom) || uom?.id === OrderUnitOfMeasure.LOAD,
      then: (schema) => {
        return yup.lazy((val) => {
          if (String(val).trim() === '' || isNil(val)) {
            return yup
              .string()
              .notRequired()
              .transform(() => null);
          }
          return schema.min(0).required();
        });
      },
      otherwise: (schema) => {
        return yup.lazy((val) => {
          if (String(val).trim() === '' || isNil(val)) {
            return yup.string().required(
              `${t('form_validation_errors.required', {
                field: t('form_fields.job_quantity'),
              })}`,
            );
          }
          return schema.min(0.01).required();
        });
      },
    }),
    unitOfMeasure: yup
      .object()
      .when(['$isHiredTruckService', 'quantity'], ([isHiredTruckService, quantity]) => {
        const hasEmptyQuantity = formQuantityFieldRepresentsEmptyQuantity(quantity);

        if (!hasEmptyQuantity) {
          return nameIdSchemaRequired.required();
        }

        if (isHiredTruckService) {
          return nameIdNonRequiredSchema.notRequired();
        }

        return nameIdSchemaRequired.required();
      }),
    priority: yup
      .number()
      .min(
        0,
        `${t('form_validation_errors.min', {
          min: 0,
          field: t('form_fields.priority'),
        })}`,
      )
      .notRequired()
      .typeError(
        t('form_validation_errors.should_be_number', {
          field: t('form_fields.priority'),
        }) || '',
      ),
    pickUpWayPoint: yup.object().when('$isHiredTruckService', {
      is: true,
      then: () => waypointSchemaOptionalSites.notRequired(),
      otherwise: () => waypointSchema.required(),
    }),
    dropOffWayPoint: yup.object().when('$isHiredTruckService', {
      is: true,
      then: () => waypointSchemaOptionalSites.notRequired(),
      otherwise: () => waypointSchema.required(),
    }),
    stagingWayPoint: waypointSchemaOptionalSites.notRequired(),
    notes: notesSchema,
    jobNotes: notesSchema,
    orderNotes: notesSchema,
    projectNotes: notesSchema,
    internalNotes: notesSchema,
    allContacts: yup.array().of(
      yup.object().shape(
        {
          name: yup
            .string()
            .trim()
            .when('phone', {
              is: (phone: string) => Boolean(phone?.trim()),
              then: () =>
                yup
                  .string()
                  .trim()
                  .required(
                    `${t('form_validation_errors.name_required_when_phone_provided')}`,
                  ),
            }),
          phone: phoneSchema,
          type: nameIdNonRequiredSchema.when(['name', 'phone'], {
            is: (name: string | null, phone: string) =>
              Boolean(name) || Boolean(phone?.trim()),
            then: () =>
              nameIdSchemaRequired.required(
                `${t('form_validation_errors.contact_type_required_when_name_or_phone_provided')}`,
              ),
          }),
          isDeleted: yup.boolean().notRequired(),
        },
        [['name', 'phone']],
      ),
    ),
    vendorAccount: yup.object().notRequired(),
    loads: yup.array().of(
      yup.object().shape({
        id: yup.string(),
        quantity: yup
          .number()
          .transform(emptyStringToNull)
          .when('$isHiredTruckService', {
            is: true,
            then: (schema) => schema.nullable().notRequired(),
            otherwise: (schema) =>
              schema
                .positive()
                .required()
                .typeError(
                  t('form_validation_errors.should_be_number', {
                    field: t('form_fields.quantity'),
                  }) || '',
                ),
          }),
        unitOfMeasure: yup.object().when('$isHiredTruckService', {
          is: true,
          then: () => nameIdNonRequiredSchema.notRequired(),
          otherwise: () => nameIdSchemaRequired.required(),
        }),
        material: yup.object().when('$isHiredTruckService', {
          is: true,
          then: () => nameIdNonRequiredSchema.notRequired(),
          otherwise: () => nameIdSchemaRequired.required(),
        }),
        waypoints: yup
          .array()
          .of(
            yup.object().shape({
              site: nameIdNonRequiredSchema.notRequired(),
            }),
          )
          .notRequired(),
        pickUpWaypoints: yup.array().of(
          yup.object().shape({
            site: yup.object().when('$isHiredTruckService', {
              is: true,
              then: () => nameIdNonRequiredSchema.notRequired(),
              otherwise: () => nameIdSchemaRequired.required(),
            }),
          }),
        ),
        dropOffWaypoints: yup.array().of(
          yup.object().shape({
            site: yup.object().when('$isHiredTruckService', {
              is: true,
              then: () => nameIdNonRequiredSchema.notRequired(),
              otherwise: () => nameIdSchemaRequired.required(),
            }),
          }),
        ),
      }),
    ),
    deletedLoadIds: yup.array().of(yup.string().required()).default([]),
    foremen: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    collaborators: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    salespeople: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    vendor: yup
      .object()
      .shape({
        label: yup.string().min(1).required(),
        value: yup.string().min(1).required(),
      })
      .nullable(),
    driver: yup
      .object()
      .shape({
        label: yup.string().min(1).required(),
        value: yup.string().min(1).required(),
      })
      .nullable(),
    fileAttachments: fileAttachmentSchema,
    requestedLoadsCount: yup.lazy((val) => {
      return String(val).trim() === ''
        ? yup.string().notRequired()
        : yup
            .number()
            .nullable()
            .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
            .integer(`${t('form_fields.must_be_integer')}`);
    }),
    equipmentTypeGrossCapacityUnitOfMeasure: yup
      .object()
      .when('equipmentTypeGrossCapacity', {
        is: (equipmentTypeGrossCapacity: string | number | null) => {
          return formQuantityFieldRepresentsEmptyQuantity(equipmentTypeGrossCapacity);
        },
        then: () => nameIdNonRequiredSchema.notRequired(),
        otherwise: () => nameIdSchemaRequired.required(),
      }),
    equipmentTypeGrossCapacity: yup.lazy((val) => {
      return String(val).trim() === ''
        ? yup.string().notRequired()
        : yup
            .number()
            .nullable()
            .min(0, `${t('form_validation_errors.minimum_is_num', { num: 0 })}`);
    }),
    recurringLoads: yup.boolean().notRequired(),
    haulerRateType: yup
      .object()
      .shape({ label: yup.string().required(), value: yup.string().required() })
      .notRequired(),
    loadUnitOfMeasure: nameIdSchemaRequired.notRequired(),
    jobTime: yup.lazy((value) => {
      const isEmpty = isNil(value) || !String(value).trim().length;

      if (isEmpty) {
        return yup.string().notRequired();
      }

      return yup.number().min(0).required();
    }),
    siteRateSchedule: yup.mixed().nullable().notRequired(),
  },
  [['unitOfMeasure', 'quantity']],
);

export const setDefaultJobValues = (job: Job) => {
  const pickUpWayPoint = job?.pickUpWayPoints?.[0];
  const dropOffWayPoint = job?.dropOffWayPoints?.[0];
  const stagingWayPoint = job?.stagingWayPoints?.[0];

  return {
    id: job.id,
    notes: job.notes,
    jobNotes: job.notes || '',
    projectNotes: '',
    orderNotes: '',
    internalNotes: job.internalNotes || '',
    quantity: job.quantity ? Number(job.quantity) || '' : null,
    unitOfMeasure: job.unitOfMeasure,
    service: job.service,
    material: job.material,
    equipment: job.equipment,
    additionalEquipment: job.additionalEquipment,
    requestedEquipmentTypeName: job.requestedEquipmentTypeName,
    jobId: job.jobId,
    jobStartAt: job.jobStartAt || '',
    pickUpWayPoint: pickUpWayPoint?.id
      ? {
          id: pickUpWayPoint?.id || '',
          type: pickUpWayPoint?.type || '',
          ordinality: pickUpWayPoint?.ordinality ?? 10,
          contact: {
            name: pickUpWayPoint?.contact?.name || '',
            phone: pickUpWayPoint?.contact?.phone || '',
          },
          site: {
            id: pickUpWayPoint?.site?.id || '',
            name: pickUpWayPoint?.site?.name || '',
            lat: pickUpWayPoint?.site?.lat,
            lon: pickUpWayPoint?.site?.lon,
          },
        }
      : null,
    dropOffWayPoint: dropOffWayPoint?.id
      ? {
          id: dropOffWayPoint?.id || '',
          type: dropOffWayPoint?.type || '',
          ordinality: dropOffWayPoint?.ordinality ?? 10,
          contact: {
            name: dropOffWayPoint?.contact?.name || '',
            phone: dropOffWayPoint?.contact?.phone || '',
          },
          site: {
            id: dropOffWayPoint?.site?.id || '',
            name: dropOffWayPoint?.site?.name || '',
            lat: dropOffWayPoint?.site?.lat,
            lon: dropOffWayPoint?.site?.lon,
          },
        }
      : null,
    stagingWayPoint: stagingWayPoint?.id
      ? {
          id: stagingWayPoint?.id || '',
          type: stagingWayPoint?.type || '',
          ordinality: stagingWayPoint?.ordinality ?? 15,
          contact: {
            name: stagingWayPoint?.contact?.name || '',
            phone: stagingWayPoint?.contact?.phone || '',
          },
          site: {
            id: stagingWayPoint?.site?.id || '',
            name: stagingWayPoint?.site?.name || '',
            lat: stagingWayPoint?.site?.lat,
            lon: stagingWayPoint?.site?.lon,
          },
        }
      : null,
    priority: job.priority || '',
    vendorAccount: {
      name: job?.vendorJobAssignment?.vendorAccount?.name || '',
      id: job?.vendorJobAssignment?.vendorAccount?.id || '',
    },
    loads: [],
    deletedLoadIds: [],
    foremen: job?.foremen || [],
    collaborators: job?.collaborators || [],
    salespeople: job?.salespeople || [],
    allContacts: job?.allContacts?.length
      ? job.allContacts.map((single) => {
          return {
            id: single.id || undefined,
            name: single.name || '',
            phone: single.phone || '',
            type: single.type || undefined,
            isDeleted: false,
          };
        })
      : [EmptyContactItem],
    vendor: job?.vendorJobAssignment?.vendorAccount
      ? {
          label: String(job.vendorJobAssignment.vendorAccount.name),
          value: String(job.vendorJobAssignment.vendorAccount.id),
        }
      : null,
    driver: job?.driver ? { label: job.driver.fullName, value: job.driver.id } : null,
    requestedLoadsCount: job.loadsCount || job.requestedLoadsCount,
    equipmentTypeGrossCapacity: job.equipmentTypeGrossCapacity ?? '',
    equipmentTypeGrossCapacityUnitOfMeasure:
      job.equipmentTypeGrossCapacityUnitOfMeasure || null,
    recurringLoads: isNil(job?.recurringLoads) ? false : job.recurringLoads,
    haulerRateType: job?.haulerRateType
      ? {
          label: getRateTypeLabel(job.haulerRateType),
          value: job.haulerRateType,
        }
      : null,
    company: job.company?.id ? { id: job.company.id } : null,
    loadUnitOfMeasure: job?.loadUnitOfMeasure || null,
    jobTime: isNil(job?.jobTime) ? null : Number(job.jobTime / 60),
    siteRateSchedule: job?.siteRateSchedule ?? null,
  } as unknown as JobFormProps;
};

export function makeCustomerSpecificJobFormSchema(
  tomlinsonOrderModalFeatureFlagEnabled: boolean,
  isSplittable?: boolean,
) {
  const maxLoadsCount = isSplittable
    ? SPLITTABLE_MAX_LOADS_COUNT
    : DEFAULT_MAX_LOADS_COUNT;

  let schema = jobFormSchema.clone().shape({
    requestedLoadsCount: yup.lazy((val) => {
      return String(val).trim() === ''
        ? yup.string().notRequired()
        : yup
            .number()
            .nullable()
            .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
            .max(
              maxLoadsCount,
              `${t('form_validation_errors.maximum_is_num', { num: maxLoadsCount })}`,
            )
            .integer(`${t('form_fields.must_be_integer')}`);
    }),
  });

  if (tomlinsonOrderModalFeatureFlagEnabled) {
    schema = schema.shape({
      haulerRateType: yup
        .object()
        .shape({ label: yup.string().required(), value: yup.string().required() })
        .required(),
    });
  }

  return schema;
}
